// Kopf, Status-Widgets und Filterzeile der Selbstauskunft-Seite (Handoff §2, §2.1, §2.2).
import { Buildings, CalendarCheck, CheckCircle, CheckSquare, LinkSimple, Sparkle, Stack, X, XCircle, type Icon } from '@phosphor-icons/react';
import { StatusKachel } from '../../../../components/ui/StatusKachel';
import { GlasAuswahl, type GlasAuswahlOption } from '../../../../components/ui/GlasAuswahl';
import { Segment } from '../../../../components/ui/Segment';
import { STATUS_LABEL, STATUS_REIHENFOLGE, type SelbstauskunftStatus } from '../../../../lib/selbstauskunft/status';
import { ZEITRAUM_LABEL, ZEITRAUM_PRESETS, zeitraumText, type ZeitraumPreset } from '../../../../lib/selbstauskunft/zeitraum';
import { STATUS_FARBE, type StatusOderAlle } from '../../../../lib/selbstauskunft/darstellung';

export const STATUS_ICON: Record<SelbstauskunftStatus, Icon> = {
  neu: Sparkle,
  geprueft: CheckSquare,
  eingeladen: CalendarCheck,
  angenommen: CheckCircle,
  abgelehnt: XCircle,
};

const PILLE = { borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 } as const;

export function SeitenKopf({ anzahl, filterText, mobil, onReset, onLinks }: {
  anzahl: number; filterText: string | null; mobil: boolean; onReset: () => void; onLinks: () => void;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between" style={{ gap: 14 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 4 }}>
        <h1 className="m-0" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02 }}>Mieterselbstauskunft</h1>
        <span className="flex flex-wrap" style={{ gap: 6, fontSize: 14, color: 'var(--ink2)' }}>
          <span>{anzahl} {anzahl === 1 ? 'Eingang' : 'Eingänge'}</span>
          {filterText && <><span>·</span><span style={{ fontWeight: 700, color: 'var(--acc)' }}>{filterText}</span></>}
        </span>
      </div>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {filterText && (
          <button type="button" onClick={onReset} data-tip="Filter zurücksetzen – Objekt und Zeitraum wieder auf „Alle“"
            className="immo-glas inline-flex items-center" style={{ ...PILLE, height: mobil ? 44 : 42, padding: '0 16px', gap: 7, fontWeight: 700, color: 'var(--ink2)' }}>
            <X size={13} weight="bold" /> Filter zurücksetzen
          </button>
        )}
        <button type="button" onClick={onLinks} data-tip="Formular-Links – Öffentliche Formularseiten anlegen, kopieren, duplizieren und bearbeiten"
          className="inline-flex items-center" style={{ ...PILLE, height: mobil ? 44 : 42, padding: '0 18px', gap: 8, fontWeight: 800, background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)' }}>
          <LinkSimple size={15} weight="bold" /> Formular-Links
        </button>
      </div>
    </header>
  );
}

export function StatusWidgets({ zaehler, aktiv, onWahl }: {
  zaehler: Record<StatusOderAlle, number>; aktiv: StatusOderAlle; onWahl: (s: StatusOderAlle) => void;
}) {
  const alle: StatusOderAlle[] = ['alle', ...STATUS_REIHENFOLGE];
  return (
    // Statusfilter und Zähler sind dasselbe Element — nicht zwei Reihen übereinander.
    <div role="group" aria-label="Status" className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))' }}>
      {alle.map((s) => {
        const label = s === 'alle' ? 'Alle' : STATUS_LABEL[s];
        return (
          <StatusKachel key={s} label={label} zahl={zaehler[s]} aktiv={aktiv === s} onClick={() => onWahl(s)}
            farbe={s === 'alle' ? 'var(--acc)' : STATUS_FARBE[s]} icon={s === 'alle' ? Stack : STATUS_ICON[s]}
            tip={`${label} – ${zaehler[s]} Eingänge in der aktuellen Auswahl`} />
        );
      })}
    </div>
  );
}

const DATUM_STIL = {
  height: 44, padding: '0 12px', borderRadius: 14, border: 'none', outline: 'none', background: 'var(--glass)', boxShadow: 'var(--glass-sh)',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: 'var(--ink)', minWidth: 0,
} as const;

export function FilterZeile({ objektWert, objektOptionen, onObjekt, zeitraum, onZeitraum, von, bis, onVon, onBis, mobil = false }: {
  objektWert: string; objektOptionen: readonly GlasAuswahlOption[]; onObjekt: (w: string) => void;
  zeitraum: ZeitraumPreset; onZeitraum: (p: ZeitraumPreset) => void;
  von: string; bis: string; onVon: (v: string) => void; onBis: (v: string) => void;
  /** Handy: Zeitraum-Knöpfe 44 px hoch */
  mobil?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
      <GlasAuswahl wert={objektWert} optionen={objektOptionen} onChange={onObjekt} icon={Buildings} ariaLabel="Objekt"
        tip="Objekt – Nur Eingänge zu diesem Objekt zeigen" style={{ flex: '1 1 220px', maxWidth: 340 }} />
      <div className="immo-scroll max-w-full overflow-x-auto" style={{ scrollbarWidth: 'none', borderRadius: 22 }}>
        <Segment ariaLabel="Zeitraum" art="akzent" hoehe={mobil ? 44 : 34} wert={zeitraum} onChange={onZeitraum}
          style={{ padding: 4, borderRadius: 22, background: 'var(--glass)', boxShadow: 'var(--glass-sh)', minWidth: 'max-content' }}
          eintraege={ZEITRAUM_PRESETS.map((p) => ({ key: p, label: ZEITRAUM_LABEL[p],
            tip: p === 'eigen' ? 'Zeitraum – Eigenes Von/Bis-Datum wählen' : `Eingang: ${ZEITRAUM_LABEL[p]}` }))} />
      </div>
      {zeitraum === 'eigen' && (
        <div className="flex items-center" style={{ gap: 8 }}>
          <input type="date" aria-label="Eingang von" value={von} onChange={(e) => onVon(e.target.value)} style={DATUM_STIL} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>bis</span>
          <input type="date" aria-label="Eingang bis" value={bis} onChange={(e) => onBis(e.target.value)} style={DATUM_STIL} />
        </div>
      )}
      <span className="ml-auto" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>Eingang · {zeitraumText(von, bis)}</span>
    </div>
  );
}
